import type {
  UploadRequestOptions,
  UploadOptions,
  UploadItem,
  UploaderAPI,
  UploadError,
  RawUploaderAPI,
  FileType,
} from "./interface";
import { uploadRequest } from "./request";
import {
  isAcceptFile,
  checkFileSizeLimit,
  getAccepts,
  getAcceptNames,
} from "./util";
import { Deferred } from "@deputy-ai/common/promise";
import { useMemoizedFn } from "ahooks";
import { useState } from "react";

let lastTimesamp = -1;
let uidIndex = 0;

function getUid() {
  const timestamp = Date.now();
  if (timestamp === lastTimesamp) {
    uidIndex += 1;
  } else {
    uidIndex = 0;
    lastTimesamp = timestamp;
  }
  return `${timestamp}_${uidIndex}`;
}

interface UseRawUploadAPIOptions
  extends Pick<
      UploadRequestOptions,
      "action" | "name" | "withCredentials" | "headers"
    >,
    Pick<UploadOptions, "onSuccess" | "onError"> {
  accept?: string;
  fileSizeLimitInMBs?: number;
}

function useRawUploaderAPI<T = any>(
  useUploaderOptions: UseRawUploadAPIOptions
): RawUploaderAPI {
  const [items, setItems] = useState<UploadItem[]>([]);
  const removeItem = (item: UploadItem) => {
    setItems(items.filter((i) => i.uid !== item.uid));
  };

  const upload = useMemoizedFn(
    ({
      file,
      onProgress,
      onSuccess,
      onError,
      withCredentials,
      headers,
    }: UploadOptions): UploadItem<T> => {
      const defered = new Deferred<T>();
      const item: UploadItem = {
        uid: getUid(),
        name: file.name,
        status: "uploading",
        originFile: file,
        progress: 0,
        promise: defered.promise,
      };
      const accepted = isAcceptFile(file, useUploaderOptions.accept);
      if (!accepted) {
        onError?.(item, "invalid_file_type");
        useUploaderOptions.onError?.(item, "invalid_file_type");
        return item;
      }
      if (!checkFileSizeLimit(file, useUploaderOptions.fileSizeLimitInMBs)) {
        onError?.(item, "file_too_large");
        useUploaderOptions.onError?.(item, "file_too_large");
        return item;
      }

      setItems((items) => [...items, item]);

      const setNewItems = () => {
        setItems((items) => {
          const index = items.indexOf(item);
          if (index >= 0) {
            return items.toSpliced(index, 1, item);
          }

          return [...items];
        });
      };

      headers ??= useUploaderOptions.headers ?? {};
      headers = {
        ...headers,
      };
      const { abort } = uploadRequest({
        name: useUploaderOptions.name,
        action: useUploaderOptions.action,
        file,
        withCredentials: withCredentials ?? useUploaderOptions.withCredentials,
        headers,
        onProgress: (progress, e) => {
          item.progress = progress;
          if (progress !== undefined) {
            setNewItems();
          }
          onProgress?.(progress, e);
        },
        onError: (response?: object) => {
          item.status = "error";
          item.response = response;
          setNewItems();
          onError?.(item);
          useUploaderOptions.onError?.(item);
        },
        onSuccess: (response?: object) => {
          item.status = "done";
          item.progress = 1.0;
          item.response = response;
          defered.resolve(response);
          setNewItems();
          onSuccess?.(item);
          useUploaderOptions.onSuccess?.(item);
        },
      });
      item.abort = abort;
      return item;
    }
  );

  return {
    items,
    removeItem,
    upload,
    accept: useUploaderOptions.accept,
  };
}

interface UseUploadAPIOptions
  extends Omit<UseRawUploadAPIOptions, "header" | "accept"> {
  acceptTypes: FileType[];
}

export function useUploaderAPI<T = any>(
  useUploaderOptions: UseUploadAPIOptions
): UploaderAPI<T> {
  const [shouldShowError, setShouldShowError] = useState<boolean>(false);
  const [error, setError] = useState<UploadError | undefined>();

  const {
    onSuccess,
    onError,
    fileSizeLimitInMBs,
    acceptTypes,
    ...restOptions
  } = useUploaderOptions;
  const accept = getAccepts(acceptTypes);

  const uploadAPI = useRawUploaderAPI<T>({
    ...restOptions,
    fileSizeLimitInMBs,
    accept,
    onSuccess: (item) => {
      uploadAPI.removeItem(item);
      onSuccess?.(item);
    },
    onError: (item, errType) => {
      uploadAPI.removeItem(item);
      if (!errType) {
        onError?.(item, errType);
        setError({
          title: "Fail to upload file",
          message: "Fail to upload file, please try again later",
        });
        setShouldShowError(true);
        return;
      }
      switch (errType) {
        case "file_too_large":
          setError({
            title: "File too large",
            message: `"${item.originFile?.name}" is too large, should be smaller than ${fileSizeLimitInMBs}MB`,
          });
          setShouldShowError(true);
          break;
        case "invalid_file_type":
          setError({
            title: "Invalid file type",
            message: `"${
              item.originFile?.name
            }" is invalid, only ${getAcceptNames(acceptTypes)} is supported`,
          });
          setShouldShowError(true);
          break;
      }
      onError?.(item, errType);
    },
  });
  return {
    ...uploadAPI,
    shouldShowError,
    setShouldShowError,
    error,
  };
}
